import { Button, Group, Paper, Stack, Text, Title, Tooltip } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { SidekickSelectionGrid } from '@/components/SidekickSelectionGrid'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'

import { ConfirmChangesModal } from './ConfirmChangesModal'
import { type SidekickOption, useSidekicks } from './useSidekicks'
import { getSidekickId, hasMskChanges, sortSidekicksForMskSelection, validateMskSelection } from './utils'

const MIN_SELECTIONS = 4 // 1 for each role to allow different sidekicks for each role
const MAX_SELECTIONS = 6

interface EditSidekicksPanelProps {
  showTutorialText: boolean
  registerUnsavedChangesChecker: (tabKey: 'sidekicks', checker: () => boolean, onDiscard: () => void) => void
}
export function EditSidekicksPanel({
  showTutorialText = false,
  registerUnsavedChangesChecker,
}: EditSidekicksPanelProps) {
  const { api } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const {
    allSidekicks,
    selectedMsks: initialSelectedMsks,
    defaultMsk: initialDefaultMsk,
    sidekickOptionToMsk,
    isLoading,
    error,
    invalidate,
  } = useSidekicks()

  // State for current selections - track sidekicks directly instead of just names
  const [selectedSidekicks, setSelectedSidekicks] = useState<SidekickOption[]>(initialSelectedMsks)
  const [defaultMsk, setDefaultMsk] = useState<string | null>(initialDefaultMsk)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isPending, setIsPending] = useState(false)

  // Initialize state when data loads
  const [syncedData, setSyncedData] = useState({ initialSelectedMsks, initialDefaultMsk, allSidekicks })
  if (
    syncedData.initialSelectedMsks !== initialSelectedMsks ||
    syncedData.initialDefaultMsk !== initialDefaultMsk ||
    syncedData.allSidekicks !== allSidekicks
  ) {
    setSyncedData({ initialSelectedMsks, initialDefaultMsk, allSidekicks })
    setSelectedSidekicks(initialSelectedMsks)
    setDefaultMsk(initialDefaultMsk)
  }

  const stableSortedSidekicks = useMemo(
    () => sortSidekicksForMskSelection(allSidekicks, initialSelectedMsks, initialDefaultMsk),
    [allSidekicks, initialSelectedMsks, initialDefaultMsk],
  )

  const updateSidekicksMutation = useMutation({
    mutationFn: async ({ msks }: { msks: SidekickOption[] }): Promise<void> => {
      const formattedMsks = msks.map(msk => sidekickOptionToMsk(msk))
      await api.updateMsks({ msks: formattedMsks })
    },
    onSuccess: async () => {
      await invalidate()
    },
    onError: error => {
      console.error('Mutation error:', error)
    },
  })

  // Computed Properties
  // ------------------------------------------------------------
  const sortedSidekickOptions = useMemo(() => {
    // Update availability in the stable sorted list based on current selections
    const selectedIds = new Set(selectedSidekicks.map(s => getSidekickId(s)))
    return stableSortedSidekicks.map(sidekick => {
      const isCurrentlySelected = selectedIds.has(getSidekickId(sidekick))
      // Only update availability if it's not selected and not originally unavailable due to other reasons
      const shouldBeAvailable =
        !isCurrentlySelected &&
        (sidekick.isAvailable ||
          initialSelectedMsks.some(initial => getSidekickId(initial) === getSidekickId(sidekick)))
      return {
        ...sidekick,
        isAvailable: shouldBeAvailable,
      }
    })
  }, [stableSortedSidekicks, selectedSidekicks, initialSelectedMsks])

  const isValidSelection = useMemo(() => {
    return validateMskSelection(selectedSidekicks.length, defaultMsk, MIN_SELECTIONS, MAX_SELECTIONS)
  }, [selectedSidekicks.length, defaultMsk])

  const hasChanges = useMemo(() => {
    const selectedMskNames = new Set(selectedSidekicks.map(msk => msk.name))
    return hasMskChanges(initialSelectedMsks, selectedSidekicks, initialDefaultMsk, defaultMsk, selectedMskNames)
  }, [initialSelectedMsks, selectedSidekicks, initialDefaultMsk, defaultMsk])

  // Handlers
  // ------------------------------------------------------------
  const handleCancel = useCallback(() => {
    setSelectedSidekicks(initialSelectedMsks)
    setDefaultMsk(initialDefaultMsk)
  }, [initialSelectedMsks, initialDefaultMsk])

  // Register unsaved changes checker with parent
  useEffect(() => {
    registerUnsavedChangesChecker('sidekicks', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  const handleApply = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmApply = async () => {
    setIsPending(true)
    try {
      // Update default flag on selected MSKs
      const msksWithDefault = selectedSidekicks.map(msk => ({
        ...msk,
        default: getSidekickId(msk) === defaultMsk,
      }))

      await updateSidekicksMutation.mutateAsync({ msks: msksWithDefault })
    } catch (error) {
      console.error('Mutation error:', error)
      // Reset state on error
      setSelectedSidekicks(initialSelectedMsks)
      setDefaultMsk(initialDefaultMsk)
    } finally {
      setIsPending(false)
      setShowConfirmModal(false)
    }
  }

  const handleSidekickToggle = (sidekick: SidekickOption) => {
    // Don't allow toggling required sidekicks
    if (sidekick.isRequired) {
      return
    }

    const isCurrentlySelected = selectedSidekicks.some(selected => getSidekickId(selected) === getSidekickId(sidekick))

    if (isCurrentlySelected) {
      // Remove from selected sidekicks
      setSelectedSidekicks(prev => prev.filter(selected => getSidekickId(selected) !== getSidekickId(sidekick)))

      // If this was the default, clear default
      if (defaultMsk === getSidekickId(sidekick)) {
        setDefaultMsk(null)
      }
    } else {
      // Only allow adding if sidekick is available (not taken as ESK) and under limit
      if (!sidekick.isAvailable) {
        return // Can't select sidekicks that are taken as ESKs
      }
      if (selectedSidekicks.length >= MAX_SELECTIONS) {
        return // Can't select more than MAX_SELECTIONS MSKs
      }

      // Add to selected sidekicks
      setSelectedSidekicks(prev => [...prev, sidekick])
    }
  }

  const handleDefaultToggle = (sidekick: SidekickOption, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering the parent button click
    const isSelected = selectedSidekicks.some(selected => getSidekickId(selected) === getSidekickId(sidekick))
    if (isSelected) {
      setDefaultMsk(getSidekickId(sidekick))
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error loading sidekicks</div>
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Stack gap="md">
          <Group gap="md" justify="space-between">
            <Group>
              <Title order={isMobile ? 6 : 4} mb={isMobile ? 'md' : undefined}>
                Select Personal Sidekicks
              </Title>
              <Title order={isMobile ? 6 : 5} c="dimmed">
                {selectedSidekicks.length} / {MAX_SELECTIONS} Selected
              </Title>
            </Group>
            <Group>
              <Button variant="outline" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </Button>
              <Tooltip label="Save becomes available when you make changes. Save or Cancel any changes.">
                <Button onClick={handleApply} disabled={!isValidSelection || !hasChanges}>
                  Save
                </Button>
              </Tooltip>
            </Group>
          </Group>
          {showTutorialText && (
            <Text size="md" c="dimmed">
              {`Select up to ${MAX_SELECTIONS} Sidekicks from which end users can choose. The Sidekick with a star is the default Personal Sidekick.`}
            </Text>
          )}
        </Stack>

        <SidekickSelectionGrid
          options={sortedSidekickOptions}
          selectedSidekicks={selectedSidekicks}
          defaultSidekick={defaultMsk}
          onSidekickToggle={handleSidekickToggle}
          onDefaultToggle={handleDefaultToggle}
          minSelections={MIN_SELECTIONS}
          maxSelections={MAX_SELECTIONS}
        />
      </Paper>

      <ConfirmChangesModal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        title="Are you sure?"
        description="Making changes to the Personal Sidekick options can impact the experience of End Users who have already chosen a Sidekick."
        isLoading={isPending}
      />
    </Stack>
  )
}
